"use client";

import { createToaster as arkCreateToaster, Toaster as ArkToaster } from "@ark-ui/react/toast";
import { ReactElement } from "react";
import { css, cx } from "styled-system/css";
import { token } from "styled-system/tokens";

import { Toast, ToastProps } from "./toast";

export interface ToasterProps {
  className?: string;
}

export interface CreateToastArgs extends ToastProps {
  /** The optional id of the toast. This can be used to update or dismiss the toast programmatically. */
  id?: string;
  /** The duration of the toast in milliseconds. This will override the default duration. */
  duration?: number;
  /** Fires when the toast starts closing, whether by the user, its timer or `dismiss`. */
  onDismiss?: () => void;
}

export interface VisibleToast {
  id?: string;
  duration: number;
  gap: number;
  meta: ToastProps;
  removeDelay: 200;
  stacked: boolean;
  type: string;
}

type ToastController = ((args: CreateToastArgs) => void) & {
  dismiss: (id?: string) => void;
  update: (id: string, props: ToastProps) => void;
  getVisibleToasts: () => VisibleToast[];
};

type CreateToasterReturn = readonly [(props: ToasterProps) => ReactElement, ToastController];

export const createToaster = (): CreateToasterReturn => {
  const toaster = arkCreateToaster({
    placement: "bottom-end",
    offsets: token("spacing.16"),
    overlap: true,
    removeDelay: 200, // This maps to durations.100
  });

  const upsertToast = ({ id, duration, onDismiss, ...props }: CreateToastArgs) => {
    const toastData: Parameters<typeof toaster.create>[0] = {
      type: duration === Infinity ? "loading" : "custom",
      duration: duration ?? 5000,
      meta: props,
      onStatusChange: ({ status }) => {
        if (status === "dismissing") onDismiss?.();
      },
    };

    // Only set the id field if it's provided
    if (id !== undefined) toastData.id = id;

    toaster.create(toastData);
  };

  const dismissToast = (id?: string) => {
    toaster.dismiss(id);
  };

  const updateToast = (id: string, props: ToastProps) => {
    toaster.update(id, {
      meta: props,
    });
  };

  const getVisibleToasts = (): VisibleToast[] => {
    return toaster.getVisibleToasts() as VisibleToast[];
  };

  /**
   * A function to create a toast. If an id is provided and there's an existing toast with that id,
   * the toast will be updated.
   *
   * Additionally, toasts can be dismissed with `toast.dismiss`.
   */
  const toast: ToastController = Object.assign(upsertToast, {
    /** Dismisses a toast by id. If no id is provided, all toasts will be dismissed. */
    dismiss: dismissToast,
    /** Updates a toast by id. */
    update: updateToast,
    /** Gets the visible toasts. */
    getVisibleToasts,
  });

  /**
   * The component from which toasts will appear.
   * This component should be placed once at the root of your application.
   */
  const Toaster = ({ className }: ToasterProps) => (
    <ArkToaster toaster={toaster} className={cx(css({ zIndex: "toast!" }), className)}>
      {(toast: { meta?: Record<string, unknown> }) => {
        const props = (toast.meta ?? {}) as unknown as ToastProps;
        return <Toast {...props} />;
      }}
    </ArkToaster>
  );

  return [Toaster, toast] as const;
};
